import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
} from "@mui/material";
import ForceGraph from "force-graph";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { formatCurrency } from "../../utils/numberFormatting";

// MUI Icons for different node types
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import AnchorIcon from "@mui/icons-material/Anchor";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import ConstructionIcon from "@mui/icons-material/Construction";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import DirectionsIcon from "@mui/icons-material/Directions";
import DnsIcon from "@mui/icons-material/Dns";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import EventIcon from "@mui/icons-material/Event";
import FlightIcon from "@mui/icons-material/Flight";
import HomeIcon from "@mui/icons-material/Home";
import LandscapeIcon from "@mui/icons-material/Landscape";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import RecyclingIcon from "@mui/icons-material/Recycling";
import SchoolIcon from "@mui/icons-material/School";
import ScienceIcon from "@mui/icons-material/Science";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrainIcon from "@mui/icons-material/Train";
import WarehouseIcon from "@mui/icons-material/Warehouse";

// Facility type to MUI icon mapping (same as map system)
const FACILITY_TYPE_ICONS: Record<string, React.ComponentType<any>> = {
  facility_complex: BusinessIcon,
  factory: PrecisionManufacturingIcon,
  production_line: SettingsIcon,
  research_and_development: ScienceIcon,
  office: CorporateFareIcon,
  power_plant: ElectricalServicesIcon,
  recycling: RecyclingIcon,
  mine: LandscapeIcon,
  oil_rig: LocalGasStationIcon,
  refinery: LocalGasStationIcon,
  store: StorefrontIcon,
  service_center: BuildIcon,
  distribution_center: LocalShippingIcon,
  warehouse: WarehouseIcon,
  construction_site: ConstructionIcon,
  transportation_hub: DirectionsIcon,
  data_center: DnsIcon,
  port: AnchorIcon,
  airport: FlightIcon,
  rail_terminal: TrainIcon,
  event_venue: EventIcon,
  residential: HomeIcon,
  educational: SchoolIcon,
  government: SecurityIcon,
  healthcare: LocalHospitalIcon,
  agricultural: AgricultureIcon,
  financial: AccountBalanceIcon,
  other_facilities: LocationOnIcon,
};

// Cache for rendered icon paths
const iconPathCache: Record<string, string> = {};

// Extract SVG path from MUI icon
const getIconPath = (IconComponent: React.ComponentType<any>): string => {
  const iconKey = IconComponent.name || "unknown";

  if (iconPathCache[iconKey]) {
    return iconPathCache[iconKey];
  }

  try {
    const iconElement = React.createElement(IconComponent, {
      style: { fontSize: "24px" },
    });
    const iconMarkup = renderToStaticMarkup(iconElement);
    const pathMatch = iconMarkup.match(/<path[^>]*d="([^"]*)"/i);
    const iconPath = pathMatch
      ? pathMatch[1]
      : "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

    iconPathCache[iconKey] = iconPath;
    return iconPath;
  } catch (error) {
    return "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";
  }
};

export interface GraphNode {
  id: string;
  name: string;
  type: "organization" | "facility" | "project" | "source";
  val?: number;
  facilityType?: string; // For facilities, this will contain the specific facility type
  hierarchy_type?: "supplier" | "customer" | "seed" | "organization";
  hierarchy_level?: number;
  color?: string;
  x?: number;
  y?: number;
  fx?: number;
  fy?: number;
}

export interface GraphLink {
  id: string;
  source: string;
  target: string;
  type: string;
  label?: string;
  strength?: number;
  color?: string;
  relation_status?: string;
  relation_summary?: string;
  relation_start?: string;
  relation_end?: string;
  confidence_score?: string;
  count?: number;
  investment_amount?: string;
  investment_currency?: string;
  ownership_share?: string;
  project_role?: string;
  sources_count?: string;
  publication_date?: string;
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

interface GraphCanvasProps {
  data: GraphData;
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  onNodeRightClick?: (node: GraphNode) => void;
  onBackgroundClick?: () => void;
  onEdgeClick?: (edge: GraphLink) => void;
  selectedNodeId?: string;
  seedEntityId?: string;
  width?: number;
  height?: number;
  layoutType?: "force" | "hierarchy";
  showEdgeLabels?: boolean;
  showNodeLabels?: boolean;
}

export const GraphCanvas: React.FC<GraphCanvasProps> = ({
  data,
  onNodeClick,
  onNodeHover,
  onNodeRightClick,
  onBackgroundClick,
  onEdgeClick,
  selectedNodeId,
  seedEntityId,
  width = 800,
  height = 600,
  layoutType = "hierarchy",
  showEdgeLabels = true,
  showNodeLabels = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const onNodeClickRef = useRef(onNodeClick);
  const onNodeHoverRef = useRef(onNodeHover);
  const onNodeRightClickRef = useRef(onNodeRightClick);
  const onBackgroundClickRef = useRef(onBackgroundClick);
  const onEdgeClickRef = useRef(onEdgeClick);

  // Edge highlight and tooltip state
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [hoveredLink, setHoveredLink] = useState<any>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const hoveredLinkRef = useRef<any>(null);
  const highlightLinksRef = useRef(new Set());
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    node: GraphNode | null;
  } | null>(null);

  // Stabilize graph data with useMemo and filter invalid links
  const stableGraphData = useMemo(() => {
    if (!data.nodes.length) return { nodes: [], links: [] };

    // Create a set of valid node IDs for quick lookup
    const validNodeIds = new Set(data.nodes.map((node) => node.id));

    // Filter links to only include those with valid source and target nodes
    const validLinks = data.links.filter((link) => {
      const sourceId =
        typeof link.source === "object" ? (link.source as any).id : link.source;
      const targetId =
        typeof link.target === "object" ? (link.target as any).id : link.target;
      return validNodeIds.has(sourceId) && validNodeIds.has(targetId);
    });

    return {
      nodes: [...data.nodes], // Create new array to ensure identity change when needed
      links: validLinks,
    };
  }, [data.nodes, data.links]);

  // Update refs when props change
  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
    onNodeHoverRef.current = onNodeHover;
    onNodeRightClickRef.current = onNodeRightClick;
    onBackgroundClickRef.current = onBackgroundClick;
    onEdgeClickRef.current = onEdgeClick;
  }, [
    onNodeClick,
    onNodeHover,
    onNodeRightClick,
    onBackgroundClick,
    onEdgeClick,
  ]);

  // Shared curvature calculation function to ensure consistency
  const calculateLinkCurvature = useCallback(
    (link: any) => {
      const sourceId =
        typeof link.source === "object" ? link.source.id : link.source;
      const targetId =
        typeof link.target === "object" ? link.target.id : link.target;

      // Create a consistent key for node pairs (always smaller ID first)
      const pairKey =
        sourceId < targetId
          ? `${sourceId}-${targetId}`
          : `${targetId}-${sourceId}`;

      // Find all links between these two nodes (in both directions)
      const parallelLinks = stableGraphData.links.filter((l: any) => {
        const lSourceId = typeof l.source === "object" ? l.source.id : l.source;
        const lTargetId = typeof l.target === "object" ? l.target.id : l.target;
        const lPairKey =
          lSourceId < lTargetId
            ? `${lSourceId}-${lTargetId}`
            : `${lTargetId}-${lSourceId}`;
        return lPairKey === pairKey;
      });

      if (parallelLinks.length <= 1) return 0; // No curvature for single edges

      // Separate links by direction for proper opposing curve assignment
      const forwardLinks = parallelLinks.filter((l: any) => {
        const lSourceId = typeof l.source === "object" ? l.source.id : l.source;
        const lTargetId = typeof l.target === "object" ? l.target.id : l.target;
        return (
          (sourceId < targetId && lSourceId < lTargetId) ||
          (sourceId > targetId && lSourceId > lTargetId)
        );
      });
      const reverseLinks = parallelLinks.filter((l: any) => {
        const lSourceId = typeof l.source === "object" ? l.source.id : l.source;
        const lTargetId = typeof l.target === "object" ? l.target.id : l.target;
        return (
          (sourceId < targetId && lSourceId > lTargetId) ||
          (sourceId > targetId && lSourceId < lTargetId)
        );
      });

      // Sort each direction group by ID for consistency
      forwardLinks.sort((a: any, b: any) => a.id.localeCompare(b.id));
      reverseLinks.sort((a: any, b: any) => a.id.localeCompare(b.id));

      // Determine which group this link belongs to and its index within that group
      const isForward = forwardLinks.some((l: any) => l.id === link.id);
      const directionLinks = isForward ? forwardLinks : reverseLinks;
      const linkIndex = directionLinks.findIndex((l: any) => l.id === link.id);

      // Calculate curvature based on direction and index
      const maxCurvature = 0.15;
      const step =
        maxCurvature / Math.max(1, Math.ceil(directionLinks.length / 2));

      // Base side: forward links curve one way, reverse links curve the other
      const baseSide = isForward ? 1 : -1;

      // Within each direction, alternate sides for multiple edges
      const alternateSide = linkIndex % 2 === 0 ? 1 : -1;
      const magnitude = step * (Math.floor(linkIndex / 2) + 1);

      return baseSide * alternateSide * magnitude;
    },
    [stableGraphData.links]
  );

  // Node styling based on hierarchy_type and selection
  const getNodeColor = useCallback(
    (node: GraphNode) => {
      // Guard against null nodes
      if (!node) return "#64748b";

      // Priority 1: Seed entity gets yellow marker (highest priority)
      if (seedEntityId === node.id) {
        return "#FFC107"; // Amber/yellow for seed entity
      }

      // Priority 2: Selected node gets gold highlighting
      if (selectedNodeId === node.id) {
        return "#FFD700"; // Gold for selected
      }

      // Priority 3: Color based on hierarchy_type if available, otherwise fall back to node type
      if (node.hierarchy_type) {
        switch (node.hierarchy_type) {
          case "supplier":
            return "#dc2626"; // Red for suppliers (upstream)
          case "customer":
            return "#059669"; // Green for customers (downstream)
          case "seed":
          case "organization":
            return "#2563eb"; // Cool blue (matching ownership edges) for org structure
          default:
            return "#64748b"; // Cool gray for unknown hierarchy types
        }
      }

      // Fallback to original type-based coloring for nodes without hierarchy_type
      switch (node.type) {
        case "organization":
          return "#0891b2"; // Deep teal/cyan for organizations
        case "facility":
          return "#475569"; // Steel blue for facilities
        case "project":
          return "#7c3aed"; // Purple/violet for projects
        case "source":
          return "#059669"; // Emerald green for sources
        default:
          return "#64748b"; // Cool gray for unknown
      }
    },
    [selectedNodeId, seedEntityId]
  );

  // Get edge color based on relationship type (cool color scheme)
  const getEdgeColor = useCallback((link: GraphLink) => {
    // Guard against null links
    if (!link) return "#64748b";

    switch (link.type) {
      // Ownership relationships - Cool blue tones
      case "full_ownership_org":
      case "full_ownership_facility":
        return "#2563eb"; // Cool blue for full ownership
      case "majority_ownership_org":
      case "majority_ownership_facility":
        return "#3b82f6"; // Lighter cool blue for majority ownership
      case "partial_ownership_org":
      case "partial_ownership_facility":
        return "#60a5fa"; // Even lighter cool blue for partial ownership

      // Supply chain relationships - Teal tones
      case "facility_supplies":
      case "org_supplies":
      case "supplies":
        return "#0d9488"; // Teal for supply chain

      // Project relationships - Purple tones
      case "facility_has_project":
      case "has_project":
        return "#7c3aed"; // Purple for project relationships

      // Mentions relationships - Cool gray
      case "mentions_facility":
      case "mentions_organization":
      case "mentions_project":
      case "mentions":
        return "#64748b"; // Cool gray for mentions

      // Partnership and joint ventures - Purple tones
      case "joint_venture_partnership":
      case "joint_venture_subsidiary":
      case "partnership":
        return "#9333ea"; // Purple for partnerships

      // Investment relationships - Cyan tones
      case "minority_investment":
      case "asset_acquisition":
        return "#0891b2"; // Cyan for investments

      // Other relationships
      case "merger":
        return "#8b5cf6"; // Violet for mergers
      case "conflict":
        return "#dc2626"; // Cool red for conflicts
      case "constructs_facility":
        return "#475569"; // Steel blue for construction
      case "has_sub_facility":
        return "#64748b"; // Cool gray for facility hierarchy
      case "moved_to":
      case "repurposed_to":
        return "#6b7280"; // Cool gray for location/purpose changes

      default:
        return "#64748b"; // Cool gray default
    }
  }, []);

  // Get MUI icon path for node type
  const getNodeIconPath = useCallback((node: GraphNode) => {
    switch (node.type) {
      case "organization":
        return getIconPath(CorporateFareIcon);
      case "facility":
        // Use facility-specific icon based on facilityType, fallback to factory
        const facilityIcon = node.facilityType
          ? FACILITY_TYPE_ICONS[node.facilityType] ||
            FACILITY_TYPE_ICONS["other_facilities"]
          : PrecisionManufacturingIcon;
        return getIconPath(facilityIcon);
      case "project":
        return getIconPath(AssignmentIcon);
      case "source":
        return getIconPath(ArticleIcon);
      default:
        return getIconPath(LocationOnIcon);
    }
  }, []);

  const getNodeSize = (node: GraphNode) => {
    if (!node) return 8;
    return node.val || 8;
  };

  // Effect to create graph instance once on mount
  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;

    // Clean up any existing canvas elements in the container (from previous renders or other libraries)
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    // Create the force graph instance once
    let graph;
    try {
      graph = new (ForceGraph as any)(containerRef.current);
      graphRef.current = graph;
      isInitializedRef.current = true;
    } catch (error) {
      return;
    }

    // Add mouse move event listener for tooltip positioning
    const handleMouseMove = (event: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        setMousePos({ x: mouseX, y: mouseY });

        // Update tooltip position immediately if there's a hovered link
        if (hoveredLinkRef.current) {
          setTooltipPos({ x: mouseX, y: mouseY });
        }
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener("mousemove", handleMouseMove);
    }

    // Prevent default context menu on the graph container
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (containerRef.current) {
      containerRef.current.addEventListener("contextmenu", handleContextMenu);
    }

    // Cleanup on unmount
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener("mousemove", handleMouseMove);
        containerRef.current.removeEventListener(
          "contextmenu",
          handleContextMenu
        );
      }

      // Proper graph cleanup to prevent Node.removeChild error
      if (graphRef.current) {
        try {
          // Stop the simulation first
          graphRef.current.pauseAnimation();
          // Clear graph data to release DOM nodes properly
          graphRef.current.graphData({ nodes: [], links: [] });
          // Call destructor if available
          if (typeof graphRef.current._destructor === "function") {
            graphRef.current._destructor();
          }
        } catch (error) {
          return;
        }
      }

      // Clear tooltip timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }

      isInitializedRef.current = false;
    };
  }, []); // Only run once on mount

  // Calculate dynamic zoom level based on hierarchy depth
  const calculateInitialZoom = useCallback(() => {
    if (!stableGraphData.nodes.length) return 1;

    // Find the range of hierarchy levels in the graph
    const hierarchyLevels = stableGraphData.nodes
      .map((node: any) => node.hierarchy_level || 0)
      .filter((level: number) => level !== undefined);

    if (hierarchyLevels.length === 0) return 1;

    // Since graph is centered at level 0, find max distance from center in either direction
    const minLevel = Math.min(...hierarchyLevels);
    const maxLevel = Math.max(...hierarchyLevels);
    const maxDistance = Math.max(Math.abs(minLevel), Math.abs(maxLevel));

    // Calculate zoom based on maximum hierarchy distance from center
    // More levels = zoom out more to fit everything
    // Base zoom levels (based on max distance from center):
    // 0-1 levels: 0.7
    // 2 levels: 0.6
    // 3 levels: 0.5
    // 4 levels: 0.4
    // 5+ levels: 0.3
    if (maxDistance < 1) return 1;
    if (maxDistance === 1) return 0.7;
    if (maxDistance === 2) return 0.5;
    if (maxDistance === 3) return 0.3;
    if (maxDistance >= 4) return 0.2;
    return 0.3;
  }, [stableGraphData.nodes]);

  // Effect to update graph configuration when props change
  useEffect(() => {
    if (!graphRef.current || !stableGraphData.nodes.length) return;

    // Calculate initial zoom based on hierarchy depth
    const initialZoom = calculateInitialZoom();

    // Update graph data and dimensions
    graphRef.current
      .graphData(stableGraphData)
      .width(width)
      .height(height)
      .backgroundColor("#262626")
      .zoom(initialZoom)
      // Node styling
      .nodeColor(getNodeColor)
      .nodeVal(getNodeSize)
      .nodeLabel((node: any) => node.name)
      .nodeCanvasObjectMode(() => "after")
      .nodeCanvasObject((node: any, ctx: any, globalScale: any) => {
        // Guard against null nodes during graph updates/cleanup
        if (!node || !ctx || node.x === undefined || node.y === undefined)
          return;

        const nodeRadius = node.val || 8;
        const iconPath = getNodeIconPath(node);

        // Add border for seed entity (drawn after default node)
        if (seedEntityId === node.id) {
          ctx.strokeStyle = "#fbbf24"; // Bright amber/gold for high contrast
          ctx.lineWidth = 3 / globalScale;
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius + 2, 0, 2 * Math.PI, false);
          ctx.stroke();
        }

        // Draw MUI icon inside the node circle
        ctx.save();
        ctx.translate(node.x, node.y);

        // Scale icon to fit inside node (about 60% of node radius)
        const iconSize = nodeRadius * 1.2;
        ctx.scale(iconSize / 24, iconSize / 24); // MUI icons are 24x24 by default

        // Create and draw the SVG path
        const path = new Path2D(iconPath);
        ctx.fillStyle = "white";
        ctx.translate(-12, -12); // Center the 24x24 icon
        ctx.fill(path);

        ctx.restore();

        // Draw node name label below the node (only if showNodeLabels is true)
        if (showNodeLabels) {
          const fontSize = Math.max(8, 10 / globalScale);
          ctx.font = `${fontSize}px Sans-Serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillStyle = "white";
          ctx.fillText(node.name, node.x, node.y + nodeRadius + 4);
        }
      })
      // Link styling
      .linkColor(getEdgeColor)
      .linkWidth((link: any) => Math.max(0.5, (link.strength || 1) * 0.8))
      .linkCurvature((link: any) => {
        // Calculate curvature for multiple edges between same nodes
        const sourceId =
          typeof link.source === "object" ? link.source.id : link.source;
        const targetId =
          typeof link.target === "object" ? link.target.id : link.target;

        // Create a consistent key for node pairs (always smaller ID first)
        const pairKey =
          sourceId < targetId
            ? `${sourceId}-${targetId}`
            : `${targetId}-${sourceId}`;

        // Find all links between these two nodes (in both directions)
        const parallelLinks = stableGraphData.links.filter((l: any) => {
          const lSourceId =
            typeof l.source === "object" ? l.source.id : l.source;
          const lTargetId =
            typeof l.target === "object" ? l.target.id : l.target;
          const lPairKey =
            lSourceId < lTargetId
              ? `${lSourceId}-${lTargetId}`
              : `${lTargetId}-${lSourceId}`;
          return lPairKey === pairKey;
        });

        if (parallelLinks.length <= 1) return 0; // No curvature for single edges

        // Separate links by direction for proper opposing curve assignment
        const forwardLinks = parallelLinks.filter((l: any) => {
          const lSourceId =
            typeof l.source === "object" ? l.source.id : l.source;
          const lTargetId =
            typeof l.target === "object" ? l.target.id : l.target;
          return (
            (sourceId < targetId && lSourceId < lTargetId) ||
            (sourceId > targetId && lSourceId > lTargetId)
          );
        });
        const reverseLinks = parallelLinks.filter((l: any) => {
          const lSourceId =
            typeof l.source === "object" ? l.source.id : l.source;
          const lTargetId =
            typeof l.target === "object" ? l.target.id : l.target;
          return (
            (sourceId < targetId && lSourceId > lTargetId) ||
            (sourceId > targetId && lSourceId < lTargetId)
          );
        });

        // Sort each direction group by ID for consistency
        forwardLinks.sort((a: any, b: any) => a.id.localeCompare(b.id));
        reverseLinks.sort((a: any, b: any) => a.id.localeCompare(b.id));

        // Determine which group this link belongs to and its index within that group
        const isForward = forwardLinks.some((l: any) => l.id === link.id);
        const directionLinks = isForward ? forwardLinks : reverseLinks;
        const linkIndex = directionLinks.findIndex(
          (l: any) => l.id === link.id
        );

        // Calculate curvature based on direction and index
        const maxCurvature = 0.15;
        const step =
          maxCurvature / Math.max(1, Math.ceil(directionLinks.length / 2));

        // Base side: forward links curve one way, reverse links curve the other
        const baseSide = isForward ? 1 : -1;

        // Within each direction, alternate sides for multiple edges
        const alternateSide = linkIndex % 2 === 0 ? 1 : -1;
        const magnitude = step * (Math.floor(linkIndex / 2) + 1);

        return baseSide * alternateSide * magnitude;
      })
      .linkCanvasObjectMode(() => "after")
      .linkCanvasObject((link: any, ctx: any, globalScale: any) => {
        // Only draw labels if showEdgeLabels is true
        if (!showEdgeLabels) return;

        const label = link.type || "related";
        if (!label) return;

        const fontSize = 10 / globalScale;
        ctx.font = `${fontSize}px Sans-Serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Use the shared curvature calculation function but invert it to fix label positioning
        const curvature = -calculateLinkCurvature(link);

        // Calculate curved path midpoint
        const dx = link.target.x - link.source.x;
        const dy = link.target.y - link.source.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Straight line midpoint
        const straightMidX = (link.source.x + link.target.x) / 2;
        const straightMidY = (link.source.y + link.target.y) / 2;

        // Calculate curved midpoint using curvature
        const perpX = -dy / distance; // Perpendicular unit vector
        const perpY = dx / distance;
        const curveOffset = curvature * distance * 0.5; // Scale curvature by distance

        const curvedMidX = straightMidX + perpX * curveOffset;
        const curvedMidY = straightMidY + perpY * curveOffset;

        // Calculate angle at curved midpoint (tangent to curve)
        const angle = Math.atan2(dy, dx);

        // Calculate perpendicular offset to move label away from edge
        const offsetDistance = 12 / globalScale;
        const perpAngle = angle + Math.PI / 2;
        const offsetX = Math.cos(perpAngle) * offsetDistance;
        const offsetY = Math.sin(perpAngle) * offsetDistance;

        // Save context and translate to curved position with offset
        ctx.save();
        ctx.translate(curvedMidX + offsetX, curvedMidY + offsetY);

        // Ensure text is always readable (not upside down)
        let rotationAngle = angle;
        if (Math.abs(angle) > Math.PI / 2) {
          rotationAngle = angle + Math.PI;
        }
        ctx.rotate(rotationAngle);

        // Draw label text (no background)
        ctx.fillStyle = "white";
        ctx.fillText(label, 0, 0);

        // Restore context
        ctx.restore();
      })
      // Arrow styling
      .linkDirectionalArrowLength(12)
      .linkDirectionalArrowRelPos(0.8)
      .linkDirectionalArrowColor(getEdgeColor)
      .onNodeClick((node: any) => {
        if (onNodeClickRef.current) {
          onNodeClickRef.current(node as GraphNode);
        }
      })
      .onNodeRightClick((node: any, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        // Always set the new context menu (React will handle closing the old one)
        setContextMenu({
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
          node: node as GraphNode,
        });
      })
      .onNodeHover((node: any) => {
        if (onNodeHoverRef.current) {
          onNodeHoverRef.current(node as GraphNode);
        }
      })
      .onBackgroundClick(() => {
        if (onBackgroundClickRef.current) {
          onBackgroundClickRef.current();
        }
      })
      .onLinkClick((link: any) => {
        if (onEdgeClickRef.current && link) {
          onEdgeClickRef.current(link as GraphLink);
        }
      })
      // Link highlighting and tooltip on hover
      .onLinkHover((link: any, prevLink: any) => {
        // Clear any existing tooltip timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
          tooltipTimeoutRef.current = null;
        }

        // Clear previous highlights
        const newHighlightLinks = new Set();

        if (link) {
          // Add current link to highlights
          newHighlightLinks.add(link);
          setHoveredLink(link);
          hoveredLinkRef.current = link;

          // Set tooltip to show after delay
          tooltipTimeoutRef.current = setTimeout(() => {
            setTooltipVisible(true);
          }, 200); // 200ms delay
        } else {
          setHoveredLink(null);
          hoveredLinkRef.current = null;
          setTooltipVisible(false);
        }

        setHighlightLinks(newHighlightLinks);
        highlightLinksRef.current = newHighlightLinks;
      })
      // Style highlighted links
      .linkWidth((link: any) => (highlightLinksRef.current.has(link) ? 3 : 1))
      // .linkColor((link: any) => highlightLinks.has(link) ? '#ffd700' : '#666')
      .enableNodeDrag(true)
      .enableZoomInteraction(true)
      .enablePanInteraction(true)
      .autoPauseRedraw(false)
      .cooldownTicks(100);

    // Configure layout based on layoutType
    if (layoutType === "hierarchy") {
      // Check if nodes have pre-calculated positions from backend
      const hasPreCalculatedPositions = stableGraphData.nodes.every(
        (node: any) =>
          node.hierarchy_x !== undefined && node.hierarchy_y !== undefined
      );

      if (hasPreCalculatedPositions) {
        // Use pre-calculated positions - disable all forces
        // graphRef.current.d3Force("charge", null); // Disable charge force
        // graphRef.current.d3Force("link", null); // Disable link force
        // graphRef.current.d3Force("center", null); // Disable center force
        // graphRef.current.d3Force("hierarchy", null); // Disable hierarchy force

        // Use pre-calculated positions from data and fix them
        stableGraphData.nodes.forEach((node: any) => {
          if (
            node.hierarchy_x !== undefined &&
            node.hierarchy_y !== undefined
          ) {
            // Use the positions from the data (backend pre-calculated)
            node.x = node.hierarchy_x; // Set current x to hierarchical x
            node.y = node.hierarchy_y; // Set current y to hierarchical y
            node.vx = 0; // Reset velocity
            node.vy = 0; // Reset velocity
            node.fx = node.hierarchy_x; // Fix x position
            node.fy = node.hierarchy_y; // Fix y position
          }
        });

        // Stop the simulation immediately since positions are fixed
        graphRef.current.d3Force("simulation")?.stop();
      } else {
        // Fallback: Calculate positions in frontend (legacy behavior)
        graphRef.current.d3Force("charge")?.strength(-300); // Reduced charge for hierarchy
        graphRef.current.d3Force("link")?.distance(150).strength(0.1); // Weaker links for hierarchy

        // Add custom positioning force for hierarchy with connection-aware positioning
        graphRef.current.d3Force("hierarchy", () => {
          // First, calculate connection-aware Y positions for each level
          const levelGroups: { [key: number]: any[] } = {};
          stableGraphData.nodes.forEach((node: any) => {
            const level = node.hierarchy_level || 0;
            if (!levelGroups[level]) levelGroups[level] = [];
            levelGroups[level].push(node);
          });

          // Sort nodes within each level based on their connections to minimize crossings
          Object.keys(levelGroups).forEach((levelKey) => {
            const level = parseInt(levelKey);
            const nodesAtLevel = levelGroups[level];

            if (nodesAtLevel.length > 1) {
              // Calculate average Y position of connected nodes from adjacent levels
              nodesAtLevel.forEach((node: any) => {
                let connectedYSum = 0;
                let connectedCount = 0;

                stableGraphData.links.forEach((link: any) => {
                  const sourceNode = stableGraphData.nodes.find(
                    (n: any) => n.id === link.source || n.id === link.source.id
                  );
                  const targetNode = stableGraphData.nodes.find(
                    (n: any) => n.id === link.target || n.id === link.target.id
                  );

                  if (sourceNode && targetNode) {
                    const sourceLevel = sourceNode.hierarchy_level || 0;
                    const targetLevel = targetNode.hierarchy_level || 0;

                    // If this node is connected to nodes in adjacent levels
                    if (
                      (sourceNode.id === node.id &&
                        Math.abs(targetLevel - level) === 1) ||
                      (targetNode.id === node.id &&
                        Math.abs(sourceLevel - level) === 1)
                    ) {
                      const connectedNode =
                        sourceNode.id === node.id ? targetNode : sourceNode;
                      if (connectedNode.y !== undefined) {
                        connectedYSum += connectedNode.y;
                        connectedCount++;
                      }
                    }
                  }
                });

                // Store the average Y position of connected nodes for sorting
                node._connectionY =
                  connectedCount > 0 ? connectedYSum / connectedCount : 0;
              });

              // Sort nodes by their connection Y positions to minimize crossings
              nodesAtLevel.sort(
                (a: any, b: any) =>
                  (a._connectionY || 0) - (b._connectionY || 0)
              );
            }
          });

          // Apply positioning forces
          stableGraphData.nodes.forEach((node: any) => {
            const hierarchyLevel = node.hierarchy_level || 0;
            const levelWidth = 800; // Increased horizontal spacing between levels
            const nodeSpacing = 200; // Vertical spacing between nodes in same level (matches backend)

            // Target X position based on hierarchy level
            const targetX = hierarchyLevel * levelWidth;

            // Get sorted nodes at this level for Y positioning
            const nodesAtLevel = levelGroups[hierarchyLevel] || [];
            const nodeIndex = nodesAtLevel.indexOf(node);
            const totalNodesAtLevel = nodesAtLevel.length;

            // Target Y position: center the sorted nodes at this level
            const targetY =
              (nodeIndex - (totalNodesAtLevel - 1) / 2) * nodeSpacing;

            // Apply strong force toward target position for hierarchy
            const alpha = 0.8; // Increased from 0.1 for stronger hierarchy positioning
            if (node.x !== undefined && node.y !== undefined) {
              node.vx = (node.vx || 0) + (targetX - node.x) * alpha;
              node.vy = (node.vy || 0) + (targetY - node.y) * alpha;
            }
          });
        });
      }
    } else {
      // Force-directed layout (default)

      // Unfix all node positions to allow force simulation
      stableGraphData.nodes.forEach((node: any) => {
        node.fx = null;
        node.fy = null;
      });

      graphRef.current.d3Force("charge")?.strength(-800); // Stronger repulsion to spread nodes
      graphRef.current.d3Force("link")?.distance(200).strength(0.3); // Longer links, weaker pull

      // Remove hierarchy force if it exists
      graphRef.current.d3Force("hierarchy", null);

      // Restart the simulation
      graphRef.current.d3ReheatSimulation();
    }
  }, [
    stableGraphData,
    width,
    height,
    layoutType,
    showEdgeLabels,
    showNodeLabels,
    seedEntityId,
    getNodeColor,
    getEdgeColor,
    getNodeIconPath,
    calculateLinkCurvature,
    calculateInitialZoom,
  ]);

  // Context menu handlers
  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleSetAsSeedNode = useCallback(() => {
    if (contextMenu?.node && onNodeRightClickRef.current) {
      onNodeRightClickRef.current(contextMenu.node);
    }
    setContextMenu(null);
  }, [contextMenu]);

  // Update graph when selectedNodeId changes
  useEffect(() => {
    if (graphRef.current) {
      graphRef.current.nodeColor(getNodeColor);
    }
  }, [selectedNodeId, getNodeColor]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#262626",
      }}
      onMouseDownCapture={(e) => {
        // Close context menu on right-click to allow new context menu to open
        if (e.button === 2 && contextMenu !== null) {
          setContextMenu(null);
        }
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      />

      {/* Custom tooltip overlay */}
      {hoveredLink && tooltipVisible && (
        <Box
          sx={{
            position: "absolute",
            left: tooltipPos.x + 10,
            top: tooltipPos.y - 10,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #444",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            maxWidth: "300px",
            fontSize: "14px",
            fontFamily: "Roboto, sans-serif",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          {/* Relationship header */}
          <Box sx={{ marginBottom: "8px", fontWeight: "bold" }}>
            Relationship:{" "}
            {typeof hoveredLink.source === "object"
              ? hoveredLink.source.name
              : "Unknown"}{" "}
            →{" "}
            {typeof hoveredLink.target === "object"
              ? hoveredLink.target.name
              : "Unknown"}
          </Box>

          {/* Relation Type */}
          {hoveredLink.type && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Type:</strong>{" "}
              {hoveredLink.type
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </Box>
          )}

          {/* Relation Status */}
          {hoveredLink.relation_status && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  color:
                    hoveredLink.relation_status === "active"
                      ? "#4ade80"
                      : hoveredLink.relation_status === "inactive" ||
                          hoveredLink.relation_status === "terminated"
                        ? "#f87171"
                        : hoveredLink.relation_status === "pending"
                          ? "#fbbf24"
                          : "white",
                }}
              >
                {hoveredLink.relation_status
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l: string) => l.toUpperCase())}
              </span>
            </Box>
          )}

          {/* Summary */}
          {hoveredLink.relation_summary && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Summary:</strong> {hoveredLink.relation_summary}
            </Box>
          )}

          {/* Confidence with sources count */}
          {hoveredLink.confidence_score && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Confidence:</strong>{" "}
              {(() => {
                try {
                  const score = parseFloat(hoveredLink.confidence_score);
                  let display = `${(score * 100).toFixed(1)}%`;

                  // Add sources count if available
                  if (
                    hoveredLink.sources_count &&
                    parseInt(hoveredLink.sources_count) > 0
                  ) {
                    const sourcesCount = parseInt(hoveredLink.sources_count);
                    const sourcesText =
                      sourcesCount === 1 ? "Source" : "Sources";
                    display += ` (${sourcesCount} ${sourcesText})`;
                  }

                  return display;
                } catch (e) {
                  return String(hoveredLink.confidence_score);
                }
              })()}
            </Box>
          )}

          {/* Investment */}
          {(hoveredLink.investment_amount ||
            hoveredLink.investment_currency) && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Investment:</strong>{" "}
              {(() => {
                if (
                  hoveredLink.investment_amount &&
                  hoveredLink.investment_currency
                ) {
                  try {
                    const amount = parseFloat(hoveredLink.investment_amount);
                    return formatCurrency(
                      amount,
                      hoveredLink.investment_currency
                    );
                  } catch (e) {
                    return `${hoveredLink.investment_amount} ${hoveredLink.investment_currency}`;
                  }
                }
                return (
                  hoveredLink.investment_amount ||
                  hoveredLink.investment_currency
                );
              })()}
            </Box>
          )}

          {/* Ownership */}
          {hoveredLink.ownership_share && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Ownership:</strong>{" "}
              {(() => {
                try {
                  const share = parseFloat(hoveredLink.ownership_share);
                  if (share <= 1.0) {
                    return `${(share * 100).toFixed(1)}%`;
                  } else {
                    return `${share.toFixed(1)}%`;
                  }
                } catch (e) {
                  return String(hoveredLink.ownership_share);
                }
              })()}
            </Box>
          )}

          {/* Timeline */}
          {hoveredLink.relation_start && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Timeline:</strong> {hoveredLink.relation_start}
              {hoveredLink.relation_end ? ` - ${hoveredLink.relation_end}` : ""}
            </Box>
          )}

          {/* Last Updated */}
          {hoveredLink.publication_date && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Last Updated:</strong> {hoveredLink.publication_date}
            </Box>
          )}

          {/* Project role */}
          {hoveredLink.project_role && (
            <Box sx={{ marginBottom: "6px" }}>
              <strong>Role:</strong> {hoveredLink.project_role}
            </Box>
          )}

          {/* Relationship count */}
          {hoveredLink.count && hoveredLink.count > 1 && (
            <Box sx={{ color: "#aaa", fontSize: "12px", marginTop: "8px" }}>
              ({hoveredLink.count} sources)
            </Box>
          )}
        </Box>
      )}

      {/* Custom context menu for right-click on nodes */}
      {contextMenu !== null && (
        <>
          {/* Backdrop to close menu when clicking outside */}
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
            }}
            onClick={handleContextMenuClose}
          />
          {/* Context menu */}
          <Paper
            sx={{
              position: "fixed",
              top: contextMenu.mouseY,
              left: contextMenu.mouseX,
              zIndex: 1300,
              minWidth: 180,
              boxShadow: 3,
            }}
          >
            <MenuItem onClick={handleSetAsSeedNode}>
              <ListItemIcon>
                <CenterFocusStrongIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Set as Seed Node</ListItemText>
            </MenuItem>
          </Paper>
        </>
      )}
    </Box>
  );
};
